import {
  createApiClient,
  parseWith,
  SESSION_SUPERSEDED_ERROR_CODE,
} from '@imobdesk/api-client'
import type { ResponseParser } from '@imobdesk/api-client'
import { getAccessToken } from './token-storage'
import { refreshToken } from '@/api/auth/auth.actions'
import { API_BASE_URL } from '@/config/api'
import { getImobdeskEntryAppHeader } from '@/lib/imobdesk-entry-app'

function isPublicAuthFailureEndpoint(endpoint: string): boolean {
  return (
    endpoint === '/api/v2/auth/login' ||
    endpoint === '/api/v2/auth/login-magic-link' ||
    endpoint === '/api/v2/auth/check-magic-link' ||
    endpoint === '/api/v2/auth/login-auto' ||
    endpoint === '/api/v2/auth/new-password' ||
    endpoint === '/api/v2/auth/reset-password' ||
    endpoint === '/api/v2/auth/register' ||
    endpoint === '/api/v2/auth/bootstrap-refresh-cookie'
  )
}

async function redirectToLoginHome(ctx?: { errorCode?: string }): Promise<void> {
  const { clearTokens } = await import('./token-storage')
  clearTokens()
  const url = new URL('/', window.location.origin)
  if (ctx?.errorCode === SESSION_SUPERSEDED_ERROR_CODE) {
    url.searchParams.set('session_ended', 'superseded')
  }
  window.location.href = url.toString()
}

const client = createApiClient({
  baseUrl: API_BASE_URL,
  entryApp: getImobdeskEntryAppHeader,
  tokenStorage: { getAccessToken },
  refreshTokenEndpoint: '/api/v2/auth/refresh-token',
  refreshToken: async () => {
    // Não limpa o access token aqui: cookie ausente é comum no login app
    // logo após magic link / antes do bootstrap. Quem decide invalidar é
    // onSessionInvalidated com contexto.
    await refreshToken()
  },
  isPublicAuthFailureEndpoint,
  refreshWithoutAccessToken: true,
  onUnauthorizedJsonError: async (_endpoint, ctx) => {
    // Se ainda há access token válido em memória, não força hard redirect —
    // deixa o caller tratar (ex.: completar-celular).
    if (getAccessToken()) {
      return
    }
    await redirectToLoginHome(ctx)
  },
  onSessionInvalidated: async (ctx) => {
    if (
      ctx.reason === 'refresh_failed' &&
      getAccessToken() &&
      ctx.errorCode !== SESSION_SUPERSEDED_ERROR_CODE
    ) {
      return false
    }
    await redirectToLoginHome({ errorCode: ctx.errorCode })
  },
})

export const apiClient = client.apiClient
export const apiClientJson = client.apiClientJson
export const apiClientJsonParsed = client.apiClientJsonParsed

export type { ResponseParser }
export { parseWith }
